import * as React from 'react'
import { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { validateField } from '@/utils/inputValidation'
import { getLocalProfiles, getLocalProfileByEmailOrCpf, saveProfiles } from '@/lib/db/repos'
import { syncAllData } from '@/lib/db/sync'
import { isTauri } from '@/lib/db/db'

interface Profile {
  id: string
  email: string
  full_name: string | null
  phone: string | null
  role: 'morador' | 'porteiro' | 'sindico' | 'administracao' | 'superadmin'
  apartment_number: string | null
  building: string | null
  is_active: boolean
  condominium_id: string | null
  cpf?: string | null
  created_at?: string
  updated_at?: string
}

interface AuthContextType {
  user: User | null
  profile: Profile | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error?: string }>
  signUp: (email: string, password: string, userData: { full_name: string; role?: string }) => Promise<{ error?: string }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error?: string }>
  updateProfile: (updates: Partial<Profile>) => Promise<{ error?: string }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const isDev = import.meta.env.DEV;

// Função auxiliar nativa para gerar hash SHA-256 da senha
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

const mapNetworkError = (error: any): string => {
  const msg = (error?.message || '').toLowerCase();
  const name = (error?.name || '').toLowerCase();
  if (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('load failed') ||
    name === 'typeerror' ||
    name === 'aborterror'
  ) {
    return 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.';
  }
  return 'Erro inesperado. Tente novamente.';
};

const mapAuthError = (error: any, mode: 'Email' | 'CPF'): string => {
  const msg = error?.message || '';
  const status = error?.status;

  if (status === 503 || status === 502 || status === 504 || status === 0) {
    return 'Servidor de autenticação indisponível no momento. Tente novamente em instantes.';
  }
  if (msg.toLowerCase().includes('failed to fetch') || msg.toLowerCase().includes('networkerror')) {
    return mapNetworkError(error);
  }

  switch (msg) {
    case 'Invalid login credentials':
      return mode === 'CPF' ? 'CPF ou senha incorretos' : 'Email ou senha incorretos';
    case 'Email not confirmed':
      return 'Por favor, confirme seu email antes de fazer login';
    case 'Too many requests':
      return 'Muitas tentativas. Tente novamente em alguns minutos.';
    default:
      return 'Erro ao fazer login. Tente novamente.';
  }
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const loading = authLoading || (user && !profile && profileLoading)

  useEffect(() => {
    let mounted = true

    // Carrega sessão local em cache para permitir login offline imediato
    const loadCachedSession = async () => {
      try {
        const cachedUserId = localStorage.getItem("itacondo_user_id");
        if (cachedUserId) {
          if (isDev) console.log("[Auth] Carregando sessão local em cache...");
          const profiles = await getLocalProfiles();
          const p = profiles.find(p => p.id === cachedUserId);
          if (p && mounted) {
            setProfile(p as Profile);
            const mockUser = {
              id: p.id,
              email: p.email || "",
              user_metadata: { full_name: p.full_name || "" }
            } as any;
            setUser(mockUser);
            setAuthLoading(false);
            
            // Sincroniza dados atualizados em segundo plano se houver conexão
            if (navigator.onLine && p.condominium_id) {
              syncAllData(p.condominium_id).catch(() => {});
            }
            return;
          }
        }
      } catch (err) {
        console.error("[Auth] Erro ao carregar sessão local:", err);
      }

      // Se não houver cache local ou falhar, tenta iniciar o escutador do Supabase (online)
      if (navigator.onLine) {
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          (event, session) => {
            if (!mounted) return
            setSession(session)
            setUser(session?.user ?? null)

            if (session?.user) {
              fetchUserProfile(session.user.id)
            } else {
              setProfile(null)
              setAuthLoading(false)
            }
          }
        )

        supabase.auth.getSession().then(({ data: { session }, error }) => {
          if (!mounted) return
          if (error) {
            supabase.auth.signOut().catch(() => {})
            setSession(null)
            setUser(null)
            setAuthLoading(false)
            return
          }
          setSession(session)
          setUser(session?.user ?? null)
          if (session?.user) {
            fetchUserProfile(session.user.id)
          } else {
            setAuthLoading(false)
          }
        }).catch(() => {
          if (mounted) setAuthLoading(false)
        })

        return () => {
          subscription.unsubscribe()
        }
      } else {
        if (mounted) setAuthLoading(false)
      }
    };

    loadCachedSession();

    return () => {
      mounted = false
    }
  }, [])

  const fetchUserProfile = async (userId: string) => {
    if (!userId) {
      setProfile(null)
      setAuthLoading(false)
      setProfileLoading(false)
      return
    }

    try {
      setProfileLoading(true)
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        console.error('Error fetching profile from cloud:', error)
        setProfile(null)
      } else {
        const profileWithFallbacks: Profile = {
          ...data,
          email: data.email || user?.email || '',
          role: data.role || 'morador',
          full_name: data.full_name || user?.user_metadata?.full_name || ''
        }
        setProfile(profileWithFallbacks)
        
        // Cacheia localmente no SQLite, mas não derruba a sessão online se o cache local falhar.
        try {
          await saveProfiles([profileWithFallbacks]);
        } catch (cacheError) {
          console.warn("[Auth] Não foi possível cachear perfil localmente:", cacheError);
        }
      }
    } catch (error) {
      console.error('Unexpected error fetching profile:', error)
      setProfile(null)
    } finally {
      setAuthLoading(false)
      setProfileLoading(false)
    }
  }

  const signIn = async (identifier: string, password: string): Promise<{ error?: string }> => {
    if (!password || password.length < 6) {
      return { error: "Senha deve ter pelo menos 6 caracteres" }
    }

    try {
      setAuthLoading(true)
      
      const cleanedIdentifier = identifier.replace(/\D/g, '');
      const isCPF = /^\d{11}$/.test(cleanedIdentifier);
      const enteredPasswordHash = await hashPassword(password);

      if (isDev) console.log(`=== LOGIN ATTEMPT (CPF: ${isCPF}) ===`);

      if (isTauri()) {
        const desktopProfile = await getLocalProfileByEmailOrCpf(isCPF ? cleanedIdentifier : identifier.trim().toLowerCase());
        if (desktopProfile?.password_hash) {
          if (enteredPasswordHash !== desktopProfile.password_hash) {
            return { error: "Senha incorreta." };
          }

          const mockUser = {
            id: desktopProfile.id,
            email: desktopProfile.email || "",
            user_metadata: { full_name: desktopProfile.full_name || "" }
          } as any;

          setUser(mockUser);
          setProfile(desktopProfile as Profile);
          setSession(null);
          localStorage.setItem("itacondo_user_id", desktopProfile.id);
          toast.success("Sessão iniciada localmente");
          return {};
        }
      }

      // 1. TENTATIVA ONLINE (SE HOUVER CONEXÃO)
      {
        try {
          let email = identifier.trim().toLowerCase();
          
          if (isCPF) {
            const { data, error: rpcError } = await supabase
              .rpc('get_email_by_cpf', { input_cpf: cleanedIdentifier })

            if (rpcError) {
              if (isDev) console.error('RPC Error Details:', rpcError)
              throw new Error("RPC_FAILED");
            }

            if (!data) {
              return { error: "CPF não encontrado ou perfil inativo." };
            }
            email = data;
          }

          const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
            email,
            password,
          })

          if (authError) {
            return { error: mapAuthError(authError, isCPF ? 'CPF' : 'Email') }
          }

          if (authData.user) {
            // Carrega perfil atualizado da nuvem
            const { data: cloudProfile, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', authData.user.id)
              .single()

            if (profileError || !cloudProfile) {
              await supabase.auth.signOut().catch(() => {});
              return { error: "Login autenticado, mas não existe perfil cadastrado para este usuário." };
            }

            // Salva credenciais criptografadas localmente no SQLite para futuros logins offline
            const profileWithHash = {
              ...cloudProfile,
              password_hash: enteredPasswordHash
            };
            try {
              await saveProfiles([profileWithHash]);
            } catch (cacheError) {
              console.warn("[Auth] Não foi possível salvar perfil no banco local:", cacheError);
            }

            // Sincroniza banco local do condomínio em segundo plano
            if (cloudProfile.condominium_id) {
              syncAllData(cloudProfile.condominium_id).catch(() => {});
            }

            setUser(authData.user)
            setProfile(cloudProfile as Profile)
            setSession(authData.session)
            
            localStorage.setItem("itacondo_user_id", authData.user.id)
            return {}
          }
        } catch (onlineError: any) {
          const message = String(onlineError?.message || "").toLowerCase();
          if (!message.includes("failed to fetch") && !message.includes("network") && !message.includes("load failed")) {
            return { error: "Não foi possível validar o login online. Tente novamente." };
          }
          console.warn("[Auth] Login online indisponível ou falhou. Tentando login local...", onlineError);
        }
      }

      // 2. FALLBACK OFFLINE (BANCO LOCAL SQLITE)
      const localProfile = await getLocalProfileByEmailOrCpf(isCPF ? cleanedIdentifier : identifier);
      
      if (!localProfile) {
        return { error: "Usuário não encontrado no banco local. Conecte-se à internet para realizar o primeiro login." }
      }

      if (!localProfile.password_hash) {
        return { error: "Primeiro acesso offline indisponível. Conecte-se à internet uma vez para registrar sua senha localmente." }
      }

      if (enteredPasswordHash !== localProfile.password_hash) {
        return { error: "Senha incorreta." }
      }

      // Login local validado!
      const mockUser = {
        id: localProfile.id,
        email: localProfile.email || "",
        user_metadata: { full_name: localProfile.full_name || "" }
      } as any;

      setUser(mockUser);
      setProfile(localProfile as Profile);
      setSession(null);
      
      localStorage.setItem("itacondo_user_id", localProfile.id);
      toast.success("Sessão iniciada localmente (Modo Offline)");
      return {};

    } catch (error: any) {
      if (isDev) console.error('Unexpected sign in error:', error)
      return { error: "Erro inesperado ao realizar login." }
    } finally {
      setAuthLoading(false)
    }
  }

  const signUp = async (
    email: string, 
    password: string, 
    userData: { full_name: string; role?: string }
  ): Promise<{ error?: string }> => {
    // Cadastro de usuário deve ser online
    if (!navigator.onLine) {
      return { error: "Você precisa estar online para se cadastrar." }
    }

    try {
      setAuthLoading(true)
      const { error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            full_name: userData.full_name.trim(),
            role: userData.role || 'morador',
            app_code: 'itacondo'
          }
        }
      })

      if (error) {
        return { error: error.message }
      }

      toast.success('Conta criada com sucesso!')
      return {}
    } catch (error) {
      console.error('Unexpected sign up error:', error)
      return { error: 'Erro inesperado. Tente novamente.' }
    } finally {
      setAuthLoading(false)
    }
  }

  const signOut = async (): Promise<void> => {
    try {
      setAuthLoading(true)
      localStorage.removeItem("itacondo_user_id")
      Object.keys(localStorage)
        .filter((key) => key.startsWith("sb-") || key.includes("supabase") || key.includes("auth-token"))
        .forEach((key) => localStorage.removeItem(key))
      sessionStorage.clear()
      
      if (navigator.onLine) {
        try {
          await supabase.auth.signOut()
        } catch (e) {
          console.warn("Erro ao fazer signout no Supabase:", e)
        }
      }
      
      setUser(null)
      setProfile(null)
      setSession(null)
      toast.success('Logout realizado com sucesso')
    } catch (error) {
      console.error('Sign out error:', error)
      setUser(null)
      setProfile(null)
      setSession(null)
      toast.success('Logout realizado com sucesso')
    } finally {
      setAuthLoading(false)
      window.location.hash = "#/login"
    }
  }

  const resetPassword = async (email: string): Promise<{ error?: string }> => {
    if (!navigator.onLine) {
      return { error: "Recuperação de senha exige conexão à internet." }
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase())
      if (error) return { error: error.message }
      toast.success('Email de recuperação enviado!')
      return {}
    } catch (error) {
      return { error: 'Erro inesperado. Tente novamente.' }
    }
  }

  const updateProfile = async (updates: Partial<Profile>): Promise<{ error?: string }> => {
    if (!user) {
      return { error: 'Usuário não autenticado' }
    }

    try {
      setProfileLoading(true)
      
      // Se online, atualiza na nuvem
      if (navigator.onLine) {
        const { error } = await supabase
          .from('profiles')
          .update(updates)
          .eq('id', user.id)
          
        if (error) throw error;
      }

      // Atualiza localmente no SQLite
      const updatedProfile = { ...profile, ...updates } as Profile;
      await saveProfiles([updatedProfile]);
      setProfile(updatedProfile);
      
      toast.success('Perfil atualizado com sucesso!')
      return {}
    } catch (error) {
      console.error('Unexpected update profile error:', error)
      return { error: 'Erro inesperado ao atualizar perfil.' }
    } finally {
      setProfileLoading(false)
    }
  }

  const contextValue: AuthContextType = {
    user,
    profile,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
